import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './@/components/ui/card';
import { Button } from './@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './@/components/ui/select';

interface WeatherEntry {
  range: [number, number];
  temperature: string;
  wind: string;
  precip: string;
}

interface WindStrengthEntry {
  range: [number, number];
  strength: string;
  sailingSpeed: string;
}

interface WindDirectionEntry {
  range: [number, number];
  direction: string;
}

interface WeatherData {
  climate: string;
  temperature: string;
  wind: string;
  windStrength: string;
  windDirection: string;
  precipitation: string;
}

interface WeatherWindGeneratorProps {
  onWeatherGenerated: (weatherData: WeatherData) => void;
  currentWeather?: WeatherData;
}

const weatherTable: Record<string, WeatherEntry[]> = {
  Cold: [
    { range: [1, 3], temperature: 'Warm', wind: 'Fair', precip: 'Clear' },
    { range: [4, 4], temperature: 'Warm', wind: 'Fair', precip: 'Rain' },
    { range: [5, 7], temperature: 'Warm', wind: 'Varies', precip: 'Clear' },
    { range: [8, 8], temperature: 'Warm', wind: 'Varies', precip: 'Rain' },
    { range: [9, 9], temperature: 'Warm', wind: 'Storm', precip: 'Rain' },
    { range: [10, 17], temperature: 'Moderate', wind: 'Fair', precip: 'Clear' },
    { range: [18, 21], temperature: 'Moderate', wind: 'Fair', precip: 'Fog' },
    { range: [22, 24], temperature: 'Moderate', wind: 'Fair', precip: 'Rain' },
    { range: [25, 30], temperature: 'Moderate', wind: 'Varies', precip: 'Clear' },
    { range: [31, 32], temperature: 'Moderate', wind: 'Varies', precip: 'Rain' },
    { range: [33, 34], temperature: 'Moderate', wind: 'Storm', precip: 'Rain' },
    { range: [35, 57], temperature: 'Cold', wind: 'Fair', precip: 'Clear' },
    { range: [58, 62], temperature: 'Cold', wind: 'Fair', precip: 'Fog' },
    { range: [63, 66], temperature: 'Cold', wind: 'Fair', precip: 'Snow' },
    { range: [67, 82], temperature: 'Cold', wind: 'Varies', precip: 'Clear' },
    { range: [83, 86], temperature: 'Cold', wind: 'Varies', precip: 'Snow' },
	{ range: [87, 89], temperature: 'Cold', wind: 'Storm', precip: 'Snow' },
	{ range: [90, 94], temperature: 'Severe cold', wind: 'Fair', precip: 'Clear' },
	{ range: [95, 98], temperature: 'Severe cold', wind: 'Varies', precip: 'Clear' },
	{ range: [99, 100], temperature: 'Severe cold', wind: 'Storm', precip: 'Clear' },
  ],
  Temperate: [
    { range: [1, 2], temperature: 'Hot', wind: 'Fair', precip: 'Clear' },
    { range: [3, 3], temperature: 'Hot', wind: 'Fair', precip: 'Rain' },
    { range: [4, 5], temperature: 'Hot', wind: 'Varies', precip: 'Clear' },
    { range: [6, 15], temperature: 'Warm', wind: 'Fair', precip: 'Clear' },
    { range: [16, 19], temperature: 'Warm', wind: 'Fair', precip: 'Rain' },
    { range: [20, 29], temperature: 'Warm', wind: 'Varies', precip: 'Clear' },
    { range: [30, 32], temperature: 'Warm', wind: 'Varies', precip: 'Rain' },
    { range: [33, 35], temperature: 'Warm', wind: 'Storm', precip: 'Rain' },
    { range: [36, 59], temperature: 'Moderate', wind: 'Fair', precip: 'Clear' },
    { range: [60, 63], temperature: 'Moderate', wind: 'Fair', precip: 'Fog' },
    { range: [64, 70], temperature: 'Moderate', wind: 'Fair', precip: 'Rain' },
    { range: [71, 80], temperature: 'Moderate', wind: 'Varies', precip: 'Clear' },
    { range: [81, 83], temperature: 'Moderate', wind: 'Varies', precip: 'Rain' },
    { range: [84, 85], temperature: 'Moderate', wind: 'Storm', precip: 'Rain' },
    { range: [86, 91], temperature: 'Cold', wind: 'Fair', precip: 'Clear' },
    { range: [92, 93], temperature: 'Cold', wind: 'Fair', precip: 'Fog' },
    { range: [94, 94], temperature: 'Cold', wind: 'Fair', precip: 'Snow' },
	{ range: [95, 98], temperature: 'Cold', wind: 'Varies', precip: 'Clear' },
	{ range: [99, 99], temperature: 'Cold', wind: 'Varies', precip: 'Snow' },
	{ range: [100, 100], temperature: 'Cold', wind: 'Storm', precip: 'Snow' },
  ],
  Warm: [
    { range: [1, 3], temperature: 'Severe heat', wind: 'Fair', precip: 'Clear' },
    { range: [4, 5], temperature: 'Severe heat', wind: 'Varies', precip: 'Clear' },
    { range: [6, 14], temperature: 'Hot', wind: 'Fair', precip: 'Clear' },
    { range: [15, 19], temperature: 'Hot', wind: 'Fair', precip: 'Rain' },
    { range: [20, 25], temperature: 'Hot', wind: 'Varies', precip: 'Clear' },
    { range: [26, 50], temperature: 'Warm', wind: 'Fair', precip: 'Clear' },
    { range: [51, 65], temperature: 'Warm', wind: 'Fair', precip: 'Rain' },
    { range: [66, 70], temperature: 'Warm', wind: 'Varies', precip: 'Clear' },
    { range: [71, 76], temperature: 'Warm', wind: 'Varies', precip: 'Rain' },
    { range: [77, 80], temperature: 'Warm', wind: 'Storm', precip: 'Rain' },
    { range: [81, 87], temperature: 'Moderate', wind: 'Fair', precip: 'Clear' },
    { range: [88, 89], temperature: 'Moderate', wind: 'Fair', precip: 'Fog' },
    { range: [90, 91], temperature: 'Moderate', wind: 'Fair', precip: 'Rain' },
    { range: [92, 96], temperature: 'Moderate', wind: 'Varies', precip: 'Clear' },
    { range: [97, 98], temperature: 'Moderate', wind: 'Varies', precip: 'Rain' },
    { range: [99, 100], temperature: 'Moderate', wind: 'Storm', precip: 'Rain' },
  ],
};

const windStrengthTable: Record<string, WindStrengthEntry[]> = {
  Fair: [
    { range: [1, 10], strength: 'None', sailingSpeed: 'None' },
    { range: [11, 65], strength: 'Light', sailingSpeed: 'x1' },
    { range: [66, 90], strength: 'Moderate', sailingSpeed: 'x2' },
    { range: [91, 99], strength: 'Strong', sailingSpeed: 'x3' },
    { range: [100, 100], strength: 'Severe (heavy)', sailingSpeed: 'x3/DC 20 sailor check' },
  ],
  Varies: [
    { range: [1, 20], strength: 'Light', sailingSpeed: 'x1' },
    { range: [21, 65], strength: 'Moderate', sailingSpeed: 'x2' },
    { range: [66, 85], strength: 'Strong', sailingSpeed: 'x3' },
    { range: [86, 98], strength: 'Severe (heavy)', sailingSpeed: 'x3/DC 20 sailor check' },
    { range: [99, 100], strength: 'Windstorm (gale)', sailingSpeed: 'x3/DC 30 sailor check' },
  ],
  Storm: [
    { range: [1, 10], strength: 'Strong', sailingSpeed: 'x3' },
    { range: [11, 50], strength: 'Severe (heavy)', sailingSpeed: 'x3/DC 20 sailor check' },
    { range: [51, 90], strength: 'Windstorm (gale)', sailingSpeed: 'x3/DC 30 sailor check' },
    { range: [91, 99], strength: 'Hurricane', sailingSpeed: 'Driven' },
    { range: [100, 100], strength: 'Dire gale', sailingSpeed: 'Driven' },
  ],
};

const windDirectionTable: WindDirectionEntry[] = [
  { range: [1, 60], direction: 'Prevailing' },
  { range: [61, 65], direction: 'North' },
  { range: [66, 70], direction: 'Northeast' },
  { range: [71, 75], direction: 'East' },
  { range: [76, 80], direction: 'Southeast' },
  { range: [81, 85], direction: 'South' },
  { range: [86, 90], direction: 'Southwest' },
  { range: [91, 95], direction: 'West' },
  { range: [96, 100], direction: 'Northwest' },
];

const rollDice = (sides: number) => Math.floor(Math.random() * sides) + 1;

const findInTable = (table: any[], roll: number) => {
  return table.find(entry => roll >= entry.range[0] && roll <= entry.range[1]);
};

const WeatherWindGenerator: React.FC<WeatherWindGeneratorProps> = ({ 
  onWeatherGenerated,
  currentWeather
}) => {
  const [climate, setClimate] = useState(currentWeather?.climate || 'Temperate');
  const [weather, setWeather] = useState<WeatherEntry | null>(null);
  const [windStrength, setWindStrength] = useState<WindStrengthEntry | null>(null);
  const [windDirection, setWindDirection] = useState<WindDirectionEntry | null>(null);
  
  // Update the display when current weather changes
  useEffect(() => {
    if (currentWeather && currentWeather.temperature && currentWeather.wind) {
      setClimate(currentWeather.climate);
      
      // Try to find the matching weather entry
      const weatherEntries = weatherTable[currentWeather.climate] || [];
      const matchingWeather = weatherEntries.find(entry => 
        entry.temperature === currentWeather.temperature && 
        entry.wind === currentWeather.wind && 
        entry.precip === currentWeather.precipitation
      );
      
      if (matchingWeather) {
        setWeather(matchingWeather);
      }
      
      // Try to find matching wind strength
      const windStrengthEntries = windStrengthTable[currentWeather.wind] || [];
      const matchingStrength = windStrengthEntries.find(entry => 
        entry.strength === currentWeather.windStrength
      );
      
      if (matchingStrength) {
        setWindStrength(matchingStrength);
      }
      
      // Try to find matching wind direction
      const matchingDirection = windDirectionTable.find(entry => 
        entry.direction === currentWeather.windDirection
      );
      
      if (matchingDirection) {
        setWindDirection(matchingDirection);
      }
    }
  }, [currentWeather]);

  const generateWeather = () => {
    const weatherRoll = rollDice(100);
    const windDirectionRoll = rollDice(100);
  
    const weatherResult = findInTable(weatherTable[climate], weatherRoll) as WeatherEntry;
    setWeather(weatherResult);
  
    const windStrengthRoll = rollDice(100);
    const windStrengthResult = findInTable(windStrengthTable[weatherResult.wind], windStrengthRoll) as WindStrengthEntry;
    setWindStrength(windStrengthResult);
  
    const windDirectionResult = findInTable(windDirectionTable, windDirectionRoll) as WindDirectionEntry;
    setWindDirection(windDirectionResult);

    // Call the onWeatherGenerated prop with the generated weather data
    onWeatherGenerated({
      climate,
      temperature: weatherResult.temperature,
      wind: weatherResult.wind,
      windStrength: windStrengthResult.strength,
      windDirection: windDirectionResult.direction,
      precipitation: weatherResult.precip,
    });
  };

  const handleClimateChange = (newClimate: string) => {
    setClimate(newClimate);
    
    // If we already have weather data, update it with the new climate
    if (weather && windStrength && windDirection) {
      onWeatherGenerated({
        climate: newClimate,
        temperature: weather.temperature,
        wind: weather.wind,
        windStrength: windStrength.strength,
        windDirection: windDirection.direction,
        precipitation: weather.precip,
      });
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto mt-4">
      <CardHeader>
        <CardTitle>Weather and Wind Generator</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Select value={climate} onValueChange={handleClimateChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select climate" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Cold">Cold</SelectItem>
              <SelectItem value="Temperate">Temperate</SelectItem>
              <SelectItem value="Warm">Warm</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={generateWeather} className="w-full">
            Generate Weather and Wind
          </Button>
          {weather && windStrength && windDirection && (
            <div className="space-y-2 bg-gray-100 dark:bg-gray-800 p-4 rounded-md text-gray-900 dark:text-gray-100">
              <p><strong>Climate:</strong> {climate}</p>
              <p><strong>Temperature:</strong> {weather.temperature}</p>
              <p><strong>Wind:</strong> {weather.wind}</p>
              <p><strong>Precipitation:</strong> {weather.precip}</p>
              <p><strong>Wind Strength:</strong> {windStrength.strength} (Sailing Speed: {windStrength.sailingSpeed})</p>
              <p><strong>Wind Direction:</strong> {windDirection.direction}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default WeatherWindGenerator;